import * as React from "react";
import { Controller } from "react-hook-form";
import { Autocomplete, createFilterOptions, TextField } from "@mui/material";
import { InputProps } from "../form/form";
import { messages } from "../../utils/i18n/i18n";
import { useTranslation } from "../../utils/i18n/lang";

interface AthleteOption {
    name: string;
    born: number;
}

interface Props<A extends AthleteOption> extends Omit<InputProps, "ref"> {
    label: React.ReactNode;
    fullWidth?: boolean;
    // account's athletes searched while typing; picking one calls onPick
    // instead of keeping the text as a new athlete's name
    athletes: Array<A>;
    onPick: (athlete: A) => void;
}

// Free-text name input that doubles as a search over existing athletes. The
// typed text is the form value (a new athlete's name); an option picked from
// the dropdown goes to onPick and the form value is left untouched.
export function AthleteNameAutocomplete<A extends AthleteOption>(p: Props<A>) {
    const m = useTranslation(msg);
    const filter = React.useMemo(() => createFilterOptions<A>(), []);
    const optionLabel = (option: A | string) =>
        typeof option === "string" ? option : `${option.name} (${option.born})`;
    const helperText =
        !p.error && p.athletes.length > 0 ? m.searchHint : p.helperText;

    return (
        <Controller
            name={p.name}
            control={p.control}
            render={({ field }) => (
                <Autocomplete<A, false, false, true>
                    freeSolo
                    options={p.athletes}
                    // only option picks matter as `value`; the text lives in
                    // inputValue, bound to the form field
                    value={null}
                    inputValue={field.value ?? ""}
                    onInputChange={(_event, text, reason) => {
                        // "reset" is MUI syncing the input to `value` (null)
                        if (reason !== "reset") {
                            field.onChange(text);
                        }
                    }}
                    onChange={(_event, picked) => {
                        if (picked != null && typeof picked !== "string") {
                            p.onPick(picked);
                        }
                    }}
                    getOptionLabel={optionLabel}
                    // no dropdown until something is typed
                    filterOptions={(options, state) =>
                        state.inputValue.trim().length === 0
                            ? []
                            : filter(options, state)
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            required={p.required}
                            error={p.error}
                            helperText={helperText}
                            label={p.label}
                            fullWidth={p.fullWidth}
                        />
                    )}
                />
            )}
        />
    );
}

const msg = messages(
    {
        searchHint:
            "Start typing to find an athlete you already added; pick one to register them.",
    },
    {
        cs: {
            searchHint:
                "Pište pro vyhledání již přidaného závodníka; výběrem ho rovnou zaregistrujete.",
        },
    }
);
